// Self-contained data adapter for the search feature.
// Reads the generated doctor index directly and enriches cards internally.
// This module intentionally does NOT import from other feature modules
// (e.g. specialists/doctorsData) so the search feature stays isolated.

import generatedDoctors from '@/generated/doctors.json';
import { withoutEmailAddressesInLocation } from '@/lib/consultants/locationPrivacy';
import type { Doctor, DoctorCard } from '@/types/doctor';
import type { BriefDoctor } from '@/types/doctor-v2';

import {
  isSearchDoctorBookable,
  mapDoctorToSearchBookingDoctor,
} from './searchEnrichment';

interface RawIndexEntry extends BriefDoctor {}

const toDoctorCard = (raw: RawIndexEntry): DoctorCard => ({
  slug: raw.slug,
  fullName: raw.fullName ?? '',
  heading: raw.heading ?? undefined,
  description: raw.description ?? undefined,
  primarySpeciality: raw.primarySpeciality ?? undefined,
  specialities: raw.specialities ?? [],
  assistantPhoneNumber: raw.assistantPhoneNumber ?? undefined,
  gender: raw.gender,
  metaKeywords: [],
  primaryPrice: raw.primaryPrice ?? null,
  image: raw.image
    ? {
        title: undefined,
        url: raw.image.url,
        width: null,
        height: null,
        contentType: null,
      }
    : undefined,
});

const isDoctor =
  (slug: string) =>
  (value: unknown): value is Doctor => {
    return (
      value !== null &&
      typeof value === 'object' &&
      'slug' in value &&
      (value as { slug: string }).slug === slug
    );
  };

const getDoctorDetail = async (slug: string): Promise<Doctor | null> => {
  const matchSlug = isDoctor(slug);

  try {
    const doctorModule = await import(`@/data/doctors/${slug}.ts`);
    const doctor = Object.values(doctorModule).find(matchSlug);
    if (doctor) return doctor;
  } catch {
    // Not found in .ts files
  }

  try {
    const clinicModule = await import(`@/data/clinics/${slug}`);
    const clinic = Object.values(clinicModule).find(matchSlug);
    if (clinic) return clinic;
  } catch {
    return null;
  }

  return null;
};

const addBookingFieldsToDoctorCard = async (
  card: DoctorCard,
): Promise<DoctorCard> => {
  const detail = await getDoctorDetail(card.slug);

  if (!detail) {
    return card;
  }

  // Doctor detail files never carry the avatar; the generated index stamps it as
  // a full CDN URL. Copy it onto the detail before mapping so `bookingDoctor`
  // carries a renderable image instead of relying on `<slug>.webp` manifest keys.
  if (!detail.image && card.image?.url) {
    detail.image = {
      title: undefined,
      url: card.image.url,
      width: null,
      height: null,
      contentType: null,
    };
  }

  const bookingDoctor = mapDoctorToSearchBookingDoctor(detail);

  return {
    ...card,
    apiId: detail.apiId ?? null,
    categoryId: detail.categoryId ?? null,
    primaryPrice:
      detail.primaryPrice !== null && detail.primaryPrice !== undefined
        ? String(detail.primaryPrice)
        : card.primaryPrice,
    duration:
      detail.duration !== null && detail.duration !== undefined
        ? String(detail.duration)
        : null,
    doctifySlug: detail.doctifySlug ?? null,
    bookingDoctor,
    isBookable:
      detail.bookable === true && isSearchDoctorBookable(bookingDoctor),
    languages: (detail.languages ?? []) as DoctorCard['languages'],
    primaryLocations: (detail.primaryLocations ?? []).map(
      withoutEmailAddressesInLocation,
    ) as DoctorCard['primaryLocations'],
    privateLocations: (detail.privateLocations ?? []).map(
      withoutEmailAddressesInLocation,
    ) as DoctorCard['privateLocations'],
  };
};

export const getSearchDoctorsList = (): DoctorCard[] => {
  const raw = generatedDoctors as unknown as RawIndexEntry[];
  const seen = new Map<string, DoctorCard>();
  for (const item of raw) {
    if (!seen.has(item.slug)) {
      seen.set(item.slug, toDoctorCard(item));
    }
  }
  return Array.from(seen.values());
};

export const getSearchDoctorsWithBookingFields = async (): Promise<
  DoctorCard[]
> => {
  return Promise.all(getSearchDoctorsList().map(addBookingFieldsToDoctorCard));
};
